export type FieldNormalizer = (value: unknown) => unknown;

const areEqual = (left: unknown, right: unknown) => {
  if (Object.is(left, right)) return true;

  if (
    left === null ||
    right === null ||
    typeof left !== "object" ||
    typeof right !== "object"
  ) {
    return false;
  }

  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
};

/**
 * Builds a partial update payload.
 *
 * An unchanged value is omitted. A value that was cleared is included as
 * `null`, provided the field normalizer maps its empty UI representation to
 * `null`.
 */
export const getChangedFields = <T extends object>(
  initial: T | undefined,
  current: T,
  fields: readonly (keyof T)[],
  normalizers: Partial<Record<keyof T, FieldNormalizer>> = {},
): Partial<T> =>
  fields.reduce<Partial<T>>((changes, field) => {
    const normalize = normalizers[field] ?? ((value: unknown) => value);
    const previousValue = normalize(initial?.[field]);
    const currentValue = normalize(current[field]);

    if (initial === undefined || !areEqual(previousValue, currentValue)) {
      changes[field] = currentValue as T[keyof T];
    }

    return changes;
  }, {});

export const normalizeOptionalText = (value: unknown): string | null => {
  if (typeof value !== "string") return null;

  const normalized = value.trim();
  return normalized && !["undefined", "null"].includes(normalized.toLowerCase())
    ? value
    : null;
};
